import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/Table';
import { Badge } from '../components/Badge';
import { Avatar, AvatarFallback } from '../components/Avatar';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '../components/Sheet';
import { ScrollArea } from '../components/ScrollArea';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Search, Filter, ClipboardCheck, AlertTriangle, CheckCircle2, Bot, Check, Edit, History, ShieldAlert, ExternalLink, Droplet, ScanLine, FileText, ClipboardList } from 'lucide-react';
const frameworks = [{
  id: 'ng89',
  name: 'NICE NG89',
  subtitle: 'VTE Prophylaxis',
  icon: Droplet,
  compliance: 91,
  nonCompliant: 23,
  target: 95,
  trend: [88, 89, 89, 90, 90, 91, 91, 91],
  status: 'amber',
  summary: 'Extended pharmacological prophylaxis: 28 days post-elective THR; 14 days post-elective TKR.'
}, {
  id: 'ng125',
  name: 'NICE NG125',
  subtitle: 'SSI Surveillance',
  icon: ScanLine,
  compliance: 87,
  nonCompliant: 14,
  target: 95,
  trend: [82, 83, 84, 85, 85, 86, 87, 87],
  status: 'amber',
  summary: 'Surgical site infection screening and documentation at 30 days and 90 days post-procedure.'
}, {
  id: 'rcs',
  name: 'RCS Op Notes',
  subtitle: 'Operative Documentation',
  icon: FileText,
  compliance: 96,
  nonCompliant: 8,
  target: 95,
  trend: [93, 94, 94, 95, 95, 96, 96, 96],
  status: 'green',
  summary: 'Royal College of Surgeons guidance: completeness of operative note across 14 required fields.'
}, {
  id: 'njr',
  name: 'NJR',
  subtitle: 'Outcomes Capture',
  icon: ClipboardList,
  compliance: 78,
  nonCompliant: 41,
  target: 95,
  trend: [72, 73, 74, 75, 76, 77, 77, 78],
  status: 'red',
  summary: 'National Joint Registry: pre- and post-operative Oxford scores plus implant data recorded for every primary joint replacement.'
}];
const mockCases = [{
  id: '1',
  patient: 'William George',
  initials: 'WG',
  procedure: 'Primary THR (R)',
  framework: 'NICE NG89',
  frameworkId: 'ng89',
  gap: 'VTE prophylaxis prescribed for 14 days (target: 28 days post-THR)',
  daysSince: 4,
  action: 'Extend prophylaxis to 28 days',
  severity: 'High',
  evidence: 'Discharge prescription: "Enoxaparin 40mg SC OD, 14-day course." NICE NG89 1.6.5 specifies extended prophylaxis of 28 days for elective hip replacement.',
  citation: 'NICE NG89, recommendation 1.6.5: Offer pharmacological VTE prophylaxis for 28 days to people having elective hip replacement surgery.',
  actionDetail: 'Letter to GP recommending 14-day extension of LMWH. Patient SMS reminder to attend nurse appointment for ongoing dose education.'
}, {
  id: '2',
  patient: 'Robert Miller',
  initials: 'RM',
  procedure: 'Hemiarthroplasty (NOF)',
  framework: 'RCS Op Notes',
  frameworkId: 'rcs',
  gap: 'Estimated blood loss field missing from operative note',
  daysSince: 2,
  action: 'Notify operating surgeon',
  severity: 'Low',
  evidence: 'Operative note recorded by ST3 but blood loss field left blank. Anaesthetic chart shows 750ml suction loss. RCS guidance requires explicit EBL documentation.',
  citation: 'RCS Good Surgical Practice (2025), Section 4.2: The operative note must include estimated blood loss, with this field never left blank.',
  actionDetail: 'Pre-populated draft sent to Mr Hartley (consultant) for addendum. EBL inferred from anaesthetic chart pending surgeon confirmation.'
}, {
  id: '3',
  patient: 'Patricia Taylor',
  initials: 'PT',
  procedure: 'Lumbar Decompression L4/5',
  framework: 'NICE NG125',
  frameworkId: 'ng125',
  gap: '30-day SSI surveillance contact not completed',
  daysSince: 32,
  action: 'Schedule telephone follow-up',
  severity: 'Medium',
  evidence: 'Procedure 08/02/2026. NG125 specifies SSI screening at 30 days. No follow-up encounter, telephone note, or wound check documented to date.',
  citation: 'NICE NG125, recommendation 1.4.3: Monitor for surgical site infection during the 30 days after surgery for procedures without an implant, and 90 days if an implant is present.',
  actionDetail: 'Auto-scheduled telephone slot with orthopaedic ANP for 14/03/2026. Patient sent SMS confirmation.'
}, {
  id: '4',
  patient: 'James Brown',
  initials: 'JB',
  procedure: 'Primary TKR (L)',
  framework: 'NJR',
  frameworkId: 'njr',
  gap: 'Post-operative Oxford Knee Score not captured at 6-month review',
  daysSince: 184,
  action: 'Send PROM questionnaire',
  severity: 'Medium',
  evidence: 'Pre-op OKS recorded (18). 6-month follow-up clinic letter present but PROM data field empty in NJR submission. NJR requires paired pre/post Oxford scores.',
  citation: 'NJR Mandatory Data Set v8: Patient-reported outcome measures (Oxford Hip/Knee Scores) must be recorded pre-operatively and at 6 months post-operatively.',
  actionDetail: 'Digital OKS questionnaire dispatched to patient portal. Reminder cascade configured at +3 and +7 days.'
}, {
  id: '5',
  patient: 'Linda Davis',
  initials: 'LD',
  procedure: 'Primary THR (L)',
  framework: 'NJR',
  frameworkId: 'njr',
  gap: 'Implant catalogue numbers not recorded',
  daysSince: 7,
  action: 'Capture from theatre log',
  severity: 'Low',
  evidence: 'Spell present in NJR submission but acetabular and femoral component catalogue numbers blank. Theatre stickers scanned and attached to anaesthetic chart but not transcribed.',
  citation: 'NJR Mandatory Data Set v8: All implant components (acetabular cup, liner, head, stem) must be recorded with manufacturer catalogue number and lot number.',
  actionDetail: 'Catalogue numbers extracted from scanned theatre stickers via OCR; pending coder confirmation before NJR resubmission.'
}, {
  id: '6',
  patient: 'Michael Wilson',
  initials: 'MW',
  procedure: 'Revision THR (L)',
  framework: 'NICE NG89',
  frameworkId: 'ng89',
  gap: 'Mechanical prophylaxis (IPC) duration not documented',
  daysSince: 5,
  action: 'Request nursing addendum',
  severity: 'Low',
  evidence: 'Pharmacological prophylaxis correctly prescribed. Nursing notes mention IPC "in use" but stop date absent. NG89 requires both modalities documented with duration.',
  citation: 'NICE NG89, recommendation 1.6.2: Continue mechanical VTE prophylaxis until the person no longer has significantly reduced mobility.',
  actionDetail: 'Auto-query raised to ward sister for IPC stop-date addendum.'
}, {
  id: '7',
  patient: 'Mary Williams',
  initials: 'MW',
  procedure: 'Knee Arthroscopy (R)',
  framework: 'NICE NG125',
  frameworkId: 'ng125',
  gap: 'Wound check at 30 days incomplete',
  daysSince: 35,
  action: 'Telephone follow-up',
  severity: 'Low',
  evidence: 'Day-case discharge 06/02/2026. GP letter on file but no SSI-specific wound check documented at 30 days.',
  citation: 'NICE NG125, recommendation 1.4.3: Monitor for surgical site infection during the 30 days after surgery for procedures without an implant.',
  actionDetail: 'Auto-scheduled telephone follow-up. Patient sent SMS prompt to flag any wound concerns.'
}];
const statusStyles: Record<string, {
  bg: string;
  text: string;
  border: string;
  dot: string;
  label: string;
}> = {
  green: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    dot: 'bg-green-500',
    label: 'On target'
  },
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    dot: 'bg-amber-500',
    label: 'Below target'
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
    dot: 'bg-red-500',
    label: 'Action needed'
  }
};
const severityBadge = (sev: string) => {
  switch (sev) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'Low':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return '';
  }
};
const frameworkChartColor = (status: string) => {
  if (status === 'green') return '#22C55E';
  if (status === 'amber') return '#C8892A';
  return '#DC2626';
};
export function ClinicalAudit() {
  const [selectedCase, setSelectedCase] = useState<(typeof mockCases)[0] | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const handleRowClick = (c: (typeof mockCases)[0]) => {
    setSelectedCase(c);
    setIsSheetOpen(true);
  };
  const selectedFramework = selectedCase ? frameworks.find(f => f.id === selectedCase.frameworkId) : null;
  return <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-4" data-id="element-162">
      <div className="flex justify-between items-start" data-id="element-163">
        <div data-id="element-164">
          <div className="flex items-center gap-3 mb-1" data-id="element-165">
            <h1 className="font-heading text-2xl sm:text-3xl font-normal text-foreground" data-id="element-166">
              Clinical Audit
            </h1>
            <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20 font-medium" data-id="element-167">
              Pathway Compliance
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm" data-id="element-168">
            Continuous audit of MSK pathway adherence against published clinical
            standards
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border" data-id="element-169">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-170" />
          <span className="text-xs font-medium text-foreground" data-id="element-171">
            Audit agent live
          </span>
          <span className="text-xs text-muted-foreground" data-id="element-172">
            · 1,247 episodes monitored
          </span>
        </div>
      </div>

      {/* Framework cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-173">
        {frameworks.map(fw => {
        const s = statusStyles[fw.status];
        return <Card key={fw.id} className="overflow-hidden" data-id="element-174">
              <CardContent className="p-6 space-y-4" data-id="element-175">
                <div className="flex justify-between items-start" data-id="element-176">
                  <div className="flex items-center gap-2" data-id="element-177">
                    <div className="p-2 rounded-full bg-secondary" data-id="element-178">
                      <fw.icon className="w-4 h-4 text-foreground" data-id="element-179" />
                    </div>
                    <div data-id="element-180">
                      <div className="text-sm font-bold text-foreground" data-id="element-181">
                        {fw.name}
                      </div>
                      <div className="text-xs text-muted-foreground" data-id="element-182">
                        {fw.subtitle}
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className={`${s.bg} ${s.text} ${s.border} text-xs`} data-id="element-183">
                    <div className={`w-1.5 h-1.5 rounded-full ${s.dot} mr-1.5`} data-id="element-184" />
                    {s.label}
                  </Badge>
                </div>

                <div className="flex items-end justify-between" data-id="element-185">
                  <div data-id="element-186">
                    <div className="font-heading text-4xl font-normal text-foreground" data-id="element-187">
                      {fw.compliance}%
                    </div>
                    <div className="text-xs text-muted-foreground mt-1" data-id="element-188">
                      Target: {fw.target}% · {fw.nonCompliant} cases flagged
                    </div>
                  </div>
                  <div className="h-12 w-24" data-id="element-189">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-190">
                      <LineChart data={fw.trend.map((val, idx) => ({
                    val,
                    idx
                  }))} data-id="element-191">
                        <Line type="monotone" dataKey="val" stroke={frameworkChartColor(fw.status)} strokeWidth={2} dot={false} isAnimationActive={false} data-id="element-192" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Progress bar to target */}
                <div data-id="element-193">
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden" data-id="element-194">
                    <div className={`h-full ${fw.status === 'green' ? 'bg-green-500' : fw.status === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                  width: `${fw.compliance}%`
                }} data-id="element-195" />
                  </div>
                </div>
              </CardContent>
            </Card>;
      })}
      </div>

      {/* Search and filter */}
      <div className="flex justify-between items-center gap-4" data-id="element-196">
        <div className="relative flex-1 max-w-md" data-id="element-197">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" data-id="element-198" />
          <Input placeholder="Search by patient, procedure, framework..." className="pl-9 bg-card" data-id="element-199" />
        </div>
        <div className="flex items-center gap-2" data-id="element-200">
          <Badge variant="outline" className="bg-card" data-id="element-201">
            Specialty: Trauma & Orthopaedics
          </Badge>
          <Button variant="outline" className="gap-2 bg-card" data-id="element-202">
            <Filter className="w-4 h-4" data-id="element-203" />
            Filter
          </Button>
        </div>
      </div>

      {/* Non-compliant cases table */}
      <Card data-id="element-204">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between" data-id="element-205">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider" data-id="element-206">
            Cases requiring action
          </h3>
          <span className="text-xs text-muted-foreground" data-id="element-207">
            {mockCases.length} of 86 flagged · sorted by severity
          </span>
        </div>
        <Table data-id="element-208">
          <TableHeader data-id="element-209">
            <TableRow data-id="element-210">
              <TableHead data-id="element-211">Patient</TableHead>
              <TableHead data-id="element-212">Procedure</TableHead>
              <TableHead data-id="element-213">Framework</TableHead>
              <TableHead data-id="element-214">Gap Detected</TableHead>
              <TableHead data-id="element-215">Days Since</TableHead>
              <TableHead data-id="element-216">Severity</TableHead>
              <TableHead data-id="element-217">Suggested Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-218">
            {mockCases.map(row => <TableRow key={row.id} className="cursor-pointer hover:bg-secondary/50" onClick={() => handleRowClick(row)} data-id="element-219">
                <TableCell className="font-medium" data-id="element-220">
                  <div className="flex items-center gap-3" data-id="element-221">
                    <Avatar className="h-8 w-8 border border-border" data-id="element-222">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs" data-id="element-223">
                        {row.initials}
                      </AvatarFallback>
                    </Avatar>
                    {row.patient}
                  </div>
                </TableCell>
                <TableCell className="text-sm" data-id="element-224">{row.procedure}</TableCell>
                <TableCell data-id="element-225">
                  <Badge variant="outline" className="font-mono text-xs bg-card" data-id="element-226">
                    {row.framework}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" data-id="element-227">
                  {row.gap}
                </TableCell>
                <TableCell data-id="element-228">
                  <span className="text-sm font-mono" data-id="element-229">{row.daysSince}d</span>
                </TableCell>
                <TableCell data-id="element-230">
                  <Badge variant="outline" className={severityBadge(row.severity)} data-id="element-231">
                    {row.severity}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-foreground max-w-[250px]" data-id="element-232">
                  <div className="flex items-center gap-2 truncate" data-id="element-233">
                    <Bot className="w-4 h-4 text-primary flex-shrink-0" data-id="element-234" />
                    <span className="truncate" data-id="element-235">{row.action}</span>
                  </div>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </Card>

      {/* Detail sheet */}
      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen} data-id="element-236">
        <SheetContent side="right" className="w-[640px] sm:max-w-none p-0 flex flex-col bg-background" data-id="element-237">
          {selectedCase && selectedFramework && <>
              <div className="p-6 border-b border-border bg-card" data-id="element-238">
                <div className="flex justify-between items-start mb-4" data-id="element-239">
                  <div data-id="element-240">
                    <SheetTitle className="font-heading text-2xl mb-1" data-id="element-241">
                      {selectedCase.patient}
                    </SheetTitle>
                    <SheetDescription className="flex items-center gap-2" data-id="element-242">
                      <span data-id="element-243">{selectedCase.procedure}</span>
                      <span data-id="element-244">•</span>
                      <span data-id="element-245">{selectedCase.daysSince} days since event</span>
                    </SheetDescription>
                  </div>
                  <Badge variant="outline" className={`text-sm px-3 py-1 ${severityBadge(selectedCase.severity)}`} data-id="element-246">
                    {selectedCase.severity} severity
                  </Badge>
                </div>
              </div>

              <ScrollArea className="flex-1 p-6" data-id="element-247">
                <div className="space-y-8" data-id="element-248">
                  {/* Framework reference */}
                  <div className="space-y-3" data-id="element-249">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-250">
                      <ClipboardCheck className="w-4 h-4 text-muted-foreground" data-id="element-251" />
                      Framework Reference
                    </h3>
                    <Card className="bg-secondary/30" data-id="element-252">
                      <CardContent className="p-4 space-y-3" data-id="element-253">
                        <div className="flex items-start justify-between gap-3" data-id="element-254">
                          <div className="flex items-center gap-3" data-id="element-255">
                            <div className="p-2 rounded-full bg-card border border-border" data-id="element-256">
                              <selectedFramework.icon className="w-4 h-4 text-foreground" data-id="element-257" />
                            </div>
                            <div data-id="element-258">
                              <div className="text-sm font-bold text-foreground" data-id="element-259">
                                {selectedFramework.name}
                              </div>
                              <div className="text-xs text-muted-foreground" data-id="element-260">
                                {selectedFramework.subtitle}
                              </div>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" data-id="element-261">
                            View standard
                            <ExternalLink className="w-3 h-3" data-id="element-262" />
                          </Button>
                        </div>
                        <p className="text-sm text-foreground italic border-l-2 border-primary/30 pl-3 py-1" data-id="element-263">
                          "{selectedCase.citation}"
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Gap detected */}
                  <div className="space-y-3" data-id="element-264">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-265">
                      <AlertTriangle className="w-4 h-4 text-amber-600" data-id="element-266" />
                      Gap Detected
                    </h3>
                    <Card className="border-amber-200 bg-amber-50/50" data-id="element-267">
                      <CardContent className="p-4 space-y-3" data-id="element-268">
                        <p className="text-sm font-medium text-amber-900" data-id="element-269">
                          {selectedCase.gap}
                        </p>
                        <div className="pt-3 border-t border-amber-200" data-id="element-270">
                          <p className="text-xs font-bold text-muted-foreground uppercase mb-2" data-id="element-271">
                            Source Evidence
                          </p>
                          <p className="text-sm text-foreground leading-relaxed" data-id="element-272">
                            {selectedCase.evidence}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Suggested action */}
                  <div className="space-y-3" data-id="element-273">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-274">
                      <Bot className="w-4 h-4 text-primary" data-id="element-275" />
                      Audit Agent Suggested Action
                    </h3>
                    <Card className="border-primary/20 shadow-sm" data-id="element-276">
                      <CardContent className="p-5 space-y-4" data-id="element-277">
                        <div data-id="element-278">
                          <p className="text-xs font-bold text-muted-foreground uppercase mb-2" data-id="element-279">
                            Recommendation
                          </p>
                          <p className="text-sm text-foreground font-medium leading-relaxed" data-id="element-280">
                            {selectedCase.action}
                          </p>
                        </div>
                        <div data-id="element-281">
                          <p className="text-xs font-bold text-muted-foreground uppercase mb-2" data-id="element-282">
                            Implementation Detail
                          </p>
                          <p className="text-sm text-foreground leading-relaxed" data-id="element-283">
                            {selectedCase.actionDetail}
                          </p>
                        </div>
                        <div className="flex gap-3 pt-4 border-t border-border" data-id="element-284">
                          <Button className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground gap-2" data-id="element-285">
                            <Check className="w-4 h-4" data-id="element-286" />
                            Approve & Action
                          </Button>
                          <Button variant="outline" className="flex-1 gap-2" data-id="element-287">
                            <Edit className="w-4 h-4" data-id="element-288" />
                            Modify
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Activity timeline */}
                  <div className="space-y-3" data-id="element-289">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2" data-id="element-290">
                      <History className="w-4 h-4 text-muted-foreground" data-id="element-291" />
                      Pathway Timeline
                    </h3>
                    <div className="pl-2 space-y-4 border-l-2 border-border ml-2" data-id="element-292">
                      <div className="relative pl-6" data-id="element-293">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center border-4 border-background" data-id="element-294">
                          <ShieldAlert className="w-2 h-2 text-white" data-id="element-295" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-296">
                          Pathway non-compliance flagged
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-297">
                          Today · {selectedFramework.name} criterion check
                        </p>
                      </div>
                      <div className="relative pl-6" data-id="element-298">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-4 border-background" data-id="element-299">
                          <Bot className="w-2 h-2 text-primary-foreground" data-id="element-300" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-301">
                          Audit agent reviewed pathway
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-302">
                          Sources: operative note, prescription record,
                          follow-up letters
                        </p>
                      </div>
                      <div className="relative pl-6" data-id="element-303">
                        <div className="absolute -left-[21px] top-1 w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center border-4 border-background" data-id="element-304">
                          <Check className="w-2 h-2 text-white" data-id="element-305" />
                        </div>
                        <p className="text-sm font-medium text-foreground" data-id="element-306">
                          Procedure performed
                        </p>
                        <p className="text-xs text-muted-foreground" data-id="element-307">
                          {selectedCase.daysSince} days ago
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </>}
        </SheetContent>
      </Sheet>
    </div>;
}